import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MdArrowBack, MdEdit, MdSave, MdImage, MdDelete, MdHistory, MdPrint,
  MdLocationOn, MdCalendarToday, MdCheckCircle, MdSwapHoriz, MdAssignment,
  MdLabel, MdCategory, MdRssFeed, MdInfo, MdWarning, MdDriveFileRenameOutline,
} from 'react-icons/md';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import IdentifierBadges from '../components/IdentifierBadges';
import { confirm } from '../lib/dialogs';
import SearchableSelect from '../components/SearchableSelect';

const STATUS_COLOR = {
  active:      { bg: '#e8f5e9', fg: '#2e7d32' },
  inactive:    { bg: '#eceff1', fg: '#546e7a' },
  maintenance: { bg: '#fff3e0', fg: '#ef6c00' },
  retired:     { bg: '#e8eaf6', fg: '#3949ab' },
  lost:        { bg: '#fce4ec', fg: '#ad1457' },
  disposed:    { bg: '#fafafa', fg: '#9e9e9e' },
};

const Toast = ({ msg, type, onClose }) => (
  <div style={{
    position: 'fixed', top: 20, right: 20, zIndex: 9999,
    padding: '12px 18px', borderRadius: 8, color: 'white',
    background: type === 'error' ? '#d32f2f' : type === 'warning' ? '#f57c00' : '#2e7d32',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    display: 'flex', alignItems: 'center', gap: 8, maxWidth: 420,
  }}>
    {msg}
    <button onClick={onClose} style={{ marginLeft: 8, background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 18 }}>×</button>
  </div>
);

/**
 * <img src> for an asset's picture.
 *
 * A picture is normally a path under this server's /uploads mount, which is
 * JWT-gated, so the token is appended as a query param for the <img> tag.
 *
 * Since v2.8.0 an imported "Image Link" column can put a COMPLETE EXTERNAL URL
 * in image_path instead. Appending the token to one of those would send the
 * viewer's session JWT to a third-party host chosen by whoever supplied the
 * import file. External URLs are already public, so they are emitted unchanged.
 * (lib/assetTemplates.js externalImageUrl() only ever stores http/https, and
 * the Android client guards the same way in AllAssetsScreen.authedImageUrl.)
 */
const assetImageSrc = (asset) => {
  const path = asset.image_medium_path || asset.image_path || '';
  if (/^https?:\/\//i.test(path)) return path;
  const token = localStorage.getItem('identium_token') || '';
  return `${path}?token=${encodeURIComponent(token)}`;
};

const AssetDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const fileRef = useRef();

  const [asset, setAsset] = useState(null);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statusInfo, setStatusInfo] = useState({ statuses: [], transitions: {} });
  const [templates, setTemplates] = useState([]);
  const [masters, setMasters] = useState([]);
  const [namingBusy, setNamingBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [statusModal, setStatusModal] = useState(null); // { to_status, reason }

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    try {
      const [a, h] = await Promise.all([
        api.get(`/assets/${id}`),
        api.get(`/assets/${id}/history`),
      ]);
      setAsset(a.data);
      setHistory(h.data);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to load asset', 'error');
    }
  }, [id]);

  useEffect(() => {
    load();
    api.get('/locations').then(r => setLocations(r.data)).catch(() => {});
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
    api.get('/assets/statuses').then(r => setStatusInfo(r.data)).catch(() => {});
    // Templates carry the resolved `name_field`; masters supply its options
    // when that field is a master_dropdown.
    api.get('/templates').then(r => setTemplates(r.data)).catch(() => {});
    api.get('/masters').then(r => setMasters(r.data)).catch(() => {});
  }, [load]);

  // ── The asset's NAME, as its own template defines it ─────────────────────
  // Which field names an asset is a per-template decision (the Tagging template
  // names it from a fixed "Chair / Bar Chair / Table" list; Computer names it
  // from the general-name master; Classic just types it). The backend resolves
  // that field and hands it over as `name_field`, so this page renders the same
  // control the registration form used rather than guessing.
  const template = useMemo(
    () => templates.find(t => t.id === asset?.template_id) || null,
    [templates, asset],
  );
  const nameField = template?.name_field || null;
  const nameOptions = useMemo(() => {
    if (!nameField) return null;
    if (nameField.field_type === 'dropdown') {
      return Array.isArray(nameField.options) ? nameField.options.filter(Boolean) : [];
    }
    if (nameField.field_type === 'master_dropdown') {
      const m = masters.find(x => x.key === nameField.master_key);
      return (m?.items || []).filter(i => i.active !== false).map(i => i.value);
    }
    return null; // free-text name field — nothing to pick from
  }, [nameField, masters]);

  // Save a rename on its own, without entering full edit mode. The PUT carries
  // `asset_name` flat; the backend maps it onto the template's name field.
  const saveAssetName = async (value) => {
    if (!asset || value === (asset.asset_name || '')) return;
    setNamingBusy(true);
    try {
      const r = await api.put(`/assets/${asset.id}`, { asset_name: value });
      setAsset(a => ({ ...a, ...r.data }));
      showToast('Asset name updated');
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Rename failed', 'error');
    } finally {
      setNamingBusy(false);
    }
  };

  if (!asset) return (
    <div className="page-container">
      <div className="loading-spinner"><div className="spinner" /><span>Loading asset…</span></div>
    </div>
  );

  const handleEdit = () => { setDraft({ ...asset }); setEditing(true); };
  const handleCancel = () => { setEditing(false); setDraft(null); };

  const handleSave = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/assets/${asset.id}`, {
        ...draft,
        location_id: draft.location_id || null,
      });
      setAsset(r.data);
      setEditing(false);
      setDraft(null);
      showToast('Asset updated');
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Update failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('image', file);
      await api.post(`/assets/${asset.id}/image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast('Image uploaded');
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Upload failed', 'error');
    } finally { setBusy(false); }
  };

  const handleImageRemove = async () => {
    if (!(await confirm('Remove asset image?'))) return;
    setBusy(true);
    try {
      await api.delete(`/assets/${asset.id}/image`);
      showToast('Image removed');
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Remove failed', 'error');
    } finally { setBusy(false); }
  };

  const handleStatusChange = async () => {
    if (!statusModal?.to_status) return;
    setBusy(true);
    try {
      const r = await api.post(`/assets/${asset.id}/status`, statusModal);
      setAsset(r.data);
      setStatusModal(null);
      showToast(`Status changed to ${r.data.status}`);
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Status change failed', 'error');
    } finally { setBusy(false); }
  };

  const handlePrintLabel = async () => {
    setBusy(true);
    try {
      const r = await api.post('/assets/labels',
        { ids: [asset.id], includeBarcode: true },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (err) {
      showToast(err.response?.data?.error || 'Print failed', 'error');
    } finally { setBusy(false); }
  };

  const allowedNext = statusInfo.transitions[asset.status] || [];
  const isAdmin = user?.role === 'admin';
  const sc = STATUS_COLOR[asset.status] || STATUS_COLOR.inactive;

  return (
    <div className="page-container">
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-outline btn-sm" onClick={() => nav('/all-assets')}>
            <MdArrowBack /> Back
          </button>
          <div>
            <div className="page-title">{asset.asset_name}</div>
            <div className="page-subtitle">
              <code style={{ fontSize: 12 }}>{asset.serial_number}</code>
              {asset.asset_tag && <> · <code style={{ fontSize: 12 }}>{asset.asset_tag}</code></>}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={handlePrintLabel} disabled={busy}>
            <MdPrint /> Print Label
          </button>
          {!editing ? (
            <button className="btn btn-primary" onClick={handleEdit}>
              <MdEdit /> Edit
            </button>
          ) : (
            <>
              <button className="btn btn-outline" onClick={handleCancel} disabled={busy}>Cancel</button>
              <button className="btn btn-success" onClick={handleSave} disabled={busy}><MdSave /> Save</button>
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16 }}>
        {/* ── LEFT: image + status workflow ─────────────────────────────────── */}
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header"><div className="card-title"><MdImage style={{ color: 'var(--accent)' }} /> Image</div></div>
            <div style={{ padding: 16, textAlign: 'center' }}>
              {asset.image_path ? (
                <img
                  src={assetImageSrc(asset)}
                  alt={asset.asset_name}
                  style={{ maxWidth: '100%', maxHeight: 280, borderRadius: 6, border: '1px solid #e0e4eb' }}
                />
              ) : (
                <div style={{
                  height: 200, border: '2px dashed #cbd2de', borderRadius: 8,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#888', fontSize: 13,
                }}>No image uploaded</div>
              )}
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
              <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'center' }}>
                <button className="btn btn-outline btn-sm" onClick={() => fileRef.current?.click()} disabled={busy}>
                  <MdImage /> {asset.image_path ? 'Replace' : 'Upload'}
                </button>
                {asset.image_path && (
                  <button className="btn btn-danger btn-sm" onClick={handleImageRemove} disabled={busy}>
                    <MdDelete /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title"><MdSwapHoriz style={{ color: 'var(--accent)' }} /> Status Workflow</div>
            </div>
            <div style={{ padding: 16 }}>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: '#777', textTransform: 'uppercase', letterSpacing: 0.5 }}>Current</div>
                <div style={{
                  display: 'inline-block', marginTop: 4, padding: '4px 12px', borderRadius: 12,
                  background: sc.bg, color: sc.fg, fontWeight: 700, textTransform: 'capitalize',
                }}>{asset.status}</div>
                {asset.status_changed_at && (
                  <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                    Since {new Date(asset.status_changed_at).toLocaleString()}
                  </div>
                )}
              </div>

              <div style={{ fontSize: 11, color: '#777', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                Transition to:
              </div>
              {allowedNext.length === 0 ? (
                <div style={{ color: '#888', fontSize: 13, fontStyle: 'italic' }}>
                  Terminal state — no further transitions
                  {isAdmin && <div style={{ fontSize: 11, marginTop: 4 }}>(admins can override below)</div>}
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {allowedNext.map(s => (
                    <button
                      key={s}
                      className="btn btn-outline btn-sm"
                      style={{ textTransform: 'capitalize' }}
                      onClick={() => setStatusModal({ to_status: s, reason: '' })}
                    >
                      → {s}
                    </button>
                  ))}
                </div>
              )}

              {isAdmin && statusInfo.statuses.length > 0 && (
                <details style={{ marginTop: 12 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 12, color: '#777' }}>Admin override (any state)</summary>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {statusInfo.statuses.filter(s => s !== asset.status).map(s => (
                      <button key={s} className="btn btn-warning btn-sm"
                        style={{ textTransform: 'capitalize', fontSize: 11 }}
                        onClick={() => setStatusModal({ to_status: s, reason: '', force: true })}
                      >→ {s}</button>
                    ))}
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>

        {/* ── RIGHT: properties + history ───────────────────────────────────── */}
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header"><div className="card-title"><MdInfo style={{ color: 'var(--accent)' }} /> Properties</div></div>
            <div style={{ padding: 16 }}>
              {editing ? (
                <EditForm
                  draft={draft} setDraft={setDraft}
                  locations={locations} categories={categories}
                  nameField={nameField} nameOptions={nameOptions}
                />
              ) : (
                <ReadView
                  asset={asset}
                  nameField={nameField}
                  nameOptions={nameOptions}
                  onRename={saveAssetName}
                  renaming={namingBusy}
                />
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title"><MdHistory style={{ color: 'var(--accent)' }} /> Timeline</div>
              <span className="badge badge-secondary">{history.length} events</span>
            </div>
            <Timeline events={history} />
          </div>
        </div>
      </div>

      {/* Status change reason modal */}
      {statusModal && (
        <div className="modal-overlay" onClick={() => setStatusModal(null)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <MdSwapHoriz /> Change status to <span style={{ textTransform: 'capitalize' }}>{statusModal.to_status}</span>
                {statusModal.force && <span className="badge badge-warning" style={{ marginLeft: 8 }}>admin override</span>}
              </div>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Reason (optional)</label>
                <textarea className="form-control" rows={3}
                  placeholder="e.g. Battery replacement scheduled"
                  value={statusModal.reason}
                  onChange={e => setStatusModal(m => ({ ...m, reason: e.target.value }))}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setStatusModal(null)} disabled={busy}>Cancel</button>
              <button className="btn btn-primary" onClick={handleStatusChange} disabled={busy}>
                <MdCheckCircle /> {busy ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Sub-components ───────────────────────────────────────────────────────────
const Field = ({ icon, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
    <div style={{ width: 24, color: 'var(--accent)', fontSize: 18, marginTop: 2 }}>{icon}</div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 11, color: '#777', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 14, color: '#222', marginTop: 2, wordBreak: 'break-word' }}>{value || <em style={{ color: '#aaa' }}>—</em>}</div>
    </div>
  </div>
);

/**
 * The Asset Name row in Properties.
 *
 * Editable in place rather than only behind the Edit button: renaming is the
 * one property people change from this page all day, and on a template whose
 * name comes from a fixed list the whole interaction is a single pick.
 * Falls back to plain text for templates that type the name freehand — there
 * is nothing to choose from, so the Edit form remains the place to change it.
 */
const AssetNameField = ({ asset, nameField, nameOptions, onRename, renaming }) => {
  const label = nameField?.label || 'Asset Name';
  const pickable = Array.isArray(nameOptions);
  if (!pickable) return <Field icon={<MdDriveFileRenameOutline />} label={label} value={asset.asset_name} />;

  // An existing name that is no longer in the list still has to be shown, or
  // the control would silently misreport the asset as unnamed.
  const current = asset.asset_name || '';
  const options = current && !nameOptions.includes(current) ? [current, ...nameOptions] : nameOptions;

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
      <div style={{ width: 24, color: 'var(--accent)', fontSize: 18, marginTop: 2 }}><MdDriveFileRenameOutline /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: '#777', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{label}</div>
        <SearchableSelect
          className="form-control"
          style={{ marginTop: 4 }}
          value={current}
          disabled={renaming}
          onChange={e => onRename(e.target.value)}
        >
          <option value="">— Not set —</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </SearchableSelect>
      </div>
    </div>
  );
};

const ReadView = ({ asset, nameField, nameOptions, onRename, renaming }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
    <div style={{ gridColumn: '1 / -1' }}>
      <AssetNameField
        asset={asset} nameField={nameField} nameOptions={nameOptions}
        onRename={onRename} renaming={renaming}
      />
    </div>
    <Field icon={<MdLabel />}        label="Asset Tag"     value={asset.asset_tag} />
    <Field icon={<MdRssFeed />}      label="RFID Tag"      value={<code style={{ color: '#1565c0' }}>{asset.rfid_tag}</code>} />
    <Field icon={<MdLabel />}        label="Serial Number" value={<code>{asset.serial_number}</code>} />
    <Field icon={<MdLabel />}        label="Barcode"       value={asset.barcode && <code>{asset.barcode}</code>} />
    <Field icon={<MdLabel />}        label="QR Code"       value={asset.qr_code && <code style={{ wordBreak: 'break-all' }}>{asset.qr_code}</code>} />
    <Field icon={<MdLabel />}        label="NFC Tag UID"   value={asset.nfc_tag && <code>{asset.nfc_tag}</code>} />
    <Field icon={<MdCategory />}     label="Category"      value={asset.category} />
    <Field icon={<MdLocationOn />}   label="Location"      value={asset.location_name} />
    <Field icon={<MdCalendarToday />} label="Purchase Date" value={asset.purchase_date} />
    <Field icon={<MdCalendarToday />} label="Registered"    value={asset.created_at && new Date(asset.created_at).toLocaleString()} />
    <div style={{ gridColumn: '1 / -1' }}>
      <Field icon={<MdInfo />} label="Identifiers registered" value={<IdentifierBadges asset={asset} />} />
    </div>
    <div style={{ gridColumn: '1 / -1' }}>
      <Field icon={<MdInfo />} label="Notes" value={asset.notes} />
    </div>
  </div>
);

const EditForm = ({ draft, setDraft, locations, categories, nameField, nameOptions }) => {
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const current = draft.asset_name || '';
  const pickable = Array.isArray(nameOptions);
  const options = pickable && current && !nameOptions.includes(current)
    ? [current, ...nameOptions]
    : nameOptions;
  return (
    <div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">{nameField?.label || 'Asset Name'} *</label>
          {pickable ? (
            <SearchableSelect className="form-control" value={current} onChange={e => set('asset_name', e.target.value)}>
              <option value="">— Not set —</option>
              {options.map(o => <option key={o} value={o}>{o}</option>)}
            </SearchableSelect>
          ) : (
            <input className="form-control" value={current} onChange={e => set('asset_name', e.target.value)} />
          )}
        </div>
        <div className="form-group">
          <label className="form-label">Asset Tag</label>
          <input className="form-control" value={draft.asset_tag || ''} onChange={e => set('asset_tag', e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Serial Number *</label>
          <input className="form-control" value={draft.serial_number || ''} onChange={e => set('serial_number', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">RFID Tag *</label>
          <input className="form-control" value={draft.rfid_tag || ''} onChange={e => set('rfid_tag', e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Barcode</label>
          <input className="form-control" value={draft.barcode || ''} onChange={e => set('barcode', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">QR Code</label>
          <input className="form-control" value={draft.qr_code || ''} onChange={e => set('qr_code', e.target.value)}
            placeholder="QR payload (URL, JSON, code …)" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">NFC Tag UID</label>
          <input className="form-control" value={draft.nfc_tag || ''} onChange={e => set('nfc_tag', e.target.value)}
            placeholder="e.g. 04:A1:B2:C3:D4:E5:F6" />
        </div>
        <div className="form-group">
          <label className="form-label">Category</label>
          <SearchableSelect className="form-control" value={draft.category || ''} onChange={e => set('category', e.target.value)}>
            <option value="">— None —</option>
            {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </SearchableSelect>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Location</label>
          <SearchableSelect className="form-control" value={draft.location_id || ''} onChange={e => set('location_id', e.target.value)}>
            <option value="">— Unassigned —</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.location_name}</option>)}
          </SearchableSelect>
        </div>
        <div className="form-group">
          <label className="form-label">Purchase Date</label>
          <input type="date" className="form-control" value={draft.purchase_date || ''} onChange={e => set('purchase_date', e.target.value)} />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Notes</label>
        <textarea className="form-control" rows={3} value={draft.notes || ''} onChange={e => set('notes', e.target.value)} />
      </div>
      <div style={{ fontSize: 11, color: '#999', marginTop: 8 }}>
        <MdWarning /> Status changes are tracked separately — use the Status Workflow panel on the left.
      </div>
    </div>
  );
};

const Timeline = ({ events }) => {
  if (events.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 13 }}>
        No history yet. Status changes, movements, and audit scans will appear here.
      </div>
    );
  }

  const ICON = {
    status:   <MdSwapHoriz />,
    movement: <MdLocationOn />,
    audit:    <MdAssignment />,
  };
  const COLOR = {
    status:   '#1565c0',
    movement: '#2e7d32',
    audit:    '#ef6c00',
  };

  return (
    <div style={{ padding: '8px 16px 16px' }}>
      {events.map((e, i) => (
        <div key={`${e.type}-${e.ev_id}`} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: i < events.length - 1 ? '1px solid #eef0f4' : 'none' }}>
          <div style={{
            width: 32, height: 32, flexShrink: 0, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `${COLOR[e.type]}15`, color: COLOR[e.type], fontSize: 18,
          }}>
            {ICON[e.type]}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: '#222' }}>
              {e.type === 'status' && (
                <>Status: <strong>{e.from_status || 'created'}</strong> → <strong style={{ textTransform: 'capitalize' }}>{e.to_status}</strong></>
              )}
              {e.type === 'movement' && (
                <>Moved: <strong>{e.from_status || 'unassigned'}</strong> → <strong>{e.to_status || 'unassigned'}</strong></>
              )}
              {e.type === 'audit' && (
                <>Scanned in audit: <strong>{e.to_status}</strong></>
              )}
            </div>
            {e.detail && <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{e.detail}</div>}
            <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
              {new Date(e.ts).toLocaleString()}
              {e.actor && ` · by ${e.actor}`}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AssetDetail;
